<template>
  <div class="space-y-6">
    <!-- Header de la pestaña de Compras -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div>
        <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
          <i class="fa fa-truck-loading text-purple-600" />
          <span>Informe de Compras & Proveedores</span>
        </h2>
        <p class="text-xs text-gray-500">Adquisiciones de mercancía, pagos efectuados y saldos pendientes a proveedores.</p>
      </div>

      <div class="flex items-center gap-2">
        <a
          :href="getPdfUrl('purchases')"
          target="_blank"
          class="px-3.5 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
        >
          <i class="fa fa-file-pdf text-rose-600" />
          <span>Exportar Compras PDF</span>
        </a>
      </div>
    </div>

    <!-- Tabla: Compras por Proveedor -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fa fa-building text-purple-500" />
          <h3 class="text-sm font-bold text-gray-900">Compras por Proveedor</h3>
        </div>
        <span class="text-xs text-gray-400 font-medium">
          Consolidado de facturas y deudas
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4">Proveedor / Empresa</th>
              <th class="py-3 px-3">RIF / Tax ID</th>
              <th class="py-3 px-3">Teléfono</th>
              <th class="py-3 px-3 text-center">Facturas</th>
              <th class="py-3 px-3 text-right">Total Compras ($)</th>
              <th class="py-3 px-3 text-right">Total Pagado ($)</th>
              <th class="py-3 px-4 text-right">Saldo Pendiente ($)</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="prov in purchases.by_provider"
              :key="prov.provider_id || prov.name"
              class="hover:bg-purple-50/20 transition-colors"
            >
              <td class="py-3 px-4 font-bold text-gray-900">
                {{ prov.name }}
              </td>
              <td class="py-3 px-3 font-mono text-gray-500">
                {{ prov.tax_id || '-' }}
              </td>
              <td class="py-3 px-3 text-gray-600">
                {{ prov.phone || '-' }}
              </td>
              <td class="py-3 px-3 text-center font-mono">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700">
                  {{ prov.count }}
                </span>
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-gray-900">
                {{ formatCurrency(prov.total) }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-semibold text-emerald-600">
                {{ formatCurrency(prov.paid) }}
              </td>
              <td class="py-3 px-4 text-right font-mono font-bold" :class="prov.pending > 0 ? 'text-rose-600' : 'text-gray-400'">
                {{ formatCurrency(prov.pending) }}
              </td>
            </tr>
            <tr v-if="!purchases.by_provider?.length">
              <td colspan="7" class="text-center py-8 text-gray-400">
                <i class="fa fa-truck-loading text-2xl text-gray-300 mb-2 block" />
                <span>No se registraron compras en el período seleccionado.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Tabla: Top Productos Comprados y Reabastecidos a Proveedores -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-4 border-b border-gray-100 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <i class="fa fa-boxes text-purple-500" />
          <h3 class="text-sm font-bold text-gray-900">Productos Más Comprados</h3>
        </div>
        <span class="text-xs text-gray-400 font-medium">
          Volumen adquirido e inversión en compras
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-gray-50 text-gray-500 uppercase font-bold text-[10px] border-b border-gray-100">
            <tr>
              <th class="py-3 px-4 text-center" style="width: 5%;">#</th>
              <th class="py-3 px-4" style="width: 40%;">Producto</th>
              <th class="py-3 px-3">Código</th>
              <th class="py-3 px-3 text-right">Unid. Compradas</th>
              <th class="py-3 px-3 text-right">Costo Promedio</th>
              <th class="py-3 px-3 text-right">Total Invertido ($)</th>
              <th class="py-3 px-4 text-right">Stock Actual</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="(item, idx) in purchases.top_purchased"
              :key="item.id"
              class="hover:bg-purple-50/20 transition-colors"
            >
              <td class="py-3 px-4 text-center font-bold text-gray-400">
                {{ idx + 1 }}
              </td>
              <td class="py-3 px-4 font-bold text-gray-900">
                {{ item.name }}
              </td>
              <td class="py-3 px-3 font-mono text-gray-500">
                {{ item.code }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-purple-700">
                {{ formatNumber(item.units_bought) }}
              </td>
              <td class="py-3 px-3 text-right font-mono text-gray-600">
                {{ formatCurrency(item.avg_price) }}
              </td>
              <td class="py-3 px-3 text-right font-mono font-bold text-gray-900">
                {{ formatCurrency(item.total_cost) }}
              </td>
              <td class="py-3 px-4 text-right font-mono font-bold">
                {{ formatNumber(item.current_stock) }}
              </td>
            </tr>
            <tr v-if="!purchases.top_purchased?.length">
              <td colspan="7" class="text-center py-6 text-gray-400">
                Sin productos comprados en este período.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PurchasesTab',

  props: {
    purchases: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getPdfUrl(type) {
      const from = this.filters.date_from || ''
      const to = this.filters.date_to || ''
      return `/reports/export/pdf?type=${type}&date_from=${from}&date_to=${to}`
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(val || 0)
    },

    formatNumber(val) {
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val || 0)
    },
  },
}
</script>
